import type { TenantLicense } from '../types/tenantLicense';
import {
  isPostLoginRedirectExcluded,
  PostLoginUserContext,
  resolveHomePageForUser,
} from './loginRedirectUtils';

export const SYSTEM_SELECTION_PATH = '/system-selection';

const KNOW_MODULE_CODE = 'KNOW';
const KNOW_HOME_PATH = '/home';
const LEGACY_KNOW_DASHBOARD_PATH = '/dashboard';

function resolveModuleDashboardUrl(license: TenantLicense, moduleCode: string): string | null {
  const url = license.dashboardUrlByModuleCode[moduleCode];
  if (!url || !url.startsWith('/')) {
    return null;
  }

  if (!license.moduleCodes.some(code => code.toUpperCase() === moduleCode.toUpperCase())) {
    return null;
  }

  // MVP Command & Focus: a home do Know passou de /dashboard para /home.
  // Licenças ainda podem trazer /dashboard (DefaultDashboardUrl legado).
  if (moduleCode.toUpperCase() === KNOW_MODULE_CODE)
  {
    if (url === LEGACY_KNOW_DASHBOARD_PATH || url === KNOW_HOME_PATH)
    {
      return KNOW_HOME_PATH;
    }
  }

  return url;
}

export function resolveDefaultModuleCode(license: TenantLicense): string | null {
  if (license.defaultProductModuleCode) {
    const match = license.moduleCodes.find(
      code => code.toUpperCase() === license.defaultProductModuleCode!.toUpperCase()
    );
    if (match) {
      return match;
    }
  }

  const knowModule = license.moduleCodes.find(
    code => code.toUpperCase() === KNOW_MODULE_CODE
  );
  if (knowModule) {
    return knowModule;
  }

  return license.moduleCodes[0] ?? null;
}

export function shouldShowSystemSelection(license: TenantLicense): boolean {
  return license.requiresSystemSelection && license.moduleCodes.length > 1;
}

export function resolvePostLoginPath(
  license: TenantLicense | null,
  userContext: PostLoginUserContext | null,
  explicitPath?: string | null
): string {
  if (explicitPath && explicitPath.startsWith('/') && !isPostLoginRedirectExcluded(explicitPath)) {
    return explicitPath;
  }

  const hasLicensedModules =
    Boolean(license?.isLicenseBypass) ||
    Boolean(
      license?.hasActiveSubscription && license.moduleCodes.length > 0
    );

  if (!license || !hasLicensedModules) {
    return resolveHomePageForUser(userContext);
  }

  if (shouldShowSystemSelection(license)) {
    return SYSTEM_SELECTION_PATH;
  }

  const moduleCode = resolveDefaultModuleCode(license);
  if (moduleCode) {
    const dashboardUrl = resolveModuleDashboardUrl(license, moduleCode);
    if (dashboardUrl) {
      return dashboardUrl;
    }
  }

  return resolveHomePageForUser(userContext);
}

export function resolveSystemSelectionTarget(
  license: TenantLicense,
  moduleCode: string
): string | null {
  return resolveModuleDashboardUrl(license, moduleCode);
}
